import { DesktopDateRangePicker as DateRangePicker } from '@mui/x-date-pickers-pro/DesktopDateRangePicker';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import * as React from 'react';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ClearIcon from '@mui/icons-material/Clear';
import { palette } from 'theme/palette';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { DateRangeValidationError } from '@mui/x-date-pickers-pro';
import { PickerChangeHandlerContext } from '@mui/x-date-pickers/models';
import { pxToRem } from 'src/common/utils/fonts';
import { DEFAULT_BORDER_RADIUS } from 'src/theme/constants';

export type DateRangeValuePiece = Date | null;
export type DateRangeValue = [DateRangeValuePiece, DateRangeValuePiece];

export type DateRangePickerProps = {
  value: [DateRangeValuePiece, DateRangeValuePiece];
  onChange: (
    value: DateRangeValue,
    context?: PickerChangeHandlerContext<DateRangeValidationError>,
  ) => void;
  minDate?: Date;
  maxDate?: Date;
  allowClearing?: boolean;
  size?: 'small' | 'large';
  numOfCalendars?: 1 | 2 | 3;
  onError?: (error: DateRangeValidationError, value: DateRangeValue) => void;
  disableFuture?: boolean;
  onClose?: () => void;
};

const BoxStyled = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  '& .MuiMultiInputDateRangeField-separator': {
    display: 'none',
  },
  '& .MuiTextField-root': {
    marginLeft: 0,
    '&.small .MuiInputBase-formControl': {
      maxWidth: theme.spacing(150),
    },
    '&.large .MuiInputBase-formControl': {
      maxWidth: theme.spacing(300),
    },
    '&:first-of-type': {
      '& .MuiInputBase-formControl fieldset': {
        borderRadius: `${DEFAULT_BORDER_RADIUS} 0 0 ${DEFAULT_BORDER_RADIUS}`,
      },
    },
    '&:last-of-type': {
      '& .MuiInputBase-formControl fieldset': {
        borderRadius: `0 ${DEFAULT_BORDER_RADIUS} ${DEFAULT_BORDER_RADIUS} 0`,
      },
    },
    '& .iconBox': {
      cursor: 'pointer',
      borderRadius: '100%',
      height: theme.spacing(28),
      width: theme.spacing(40),
      '&:hover': {
        backgroundColor: '#f0f0f0',
      },
      '& .calendarIcon': {
        height: theme.spacing(22),
        width: theme.spacing(22),
        marginTop: theme.spacing(2),
        marginLeft: theme.spacing(3),
      },
    },
  },
  '& .clearIcon': {
    position: 'absolute',
    right: theme.spacing(42),
    height: theme.spacing(22),
    width: theme.spacing(22),
    borderRight: `1px solid ${palette.border}`,
    cursor: 'pointer',
  },
  '& .MuiInputLabel-root': {
    opacity: '60%',
    top: theme.spacing(-6),
    transform: `translate(${theme.spacing(14)}, ${theme.spacing(16)}) scale(1)`,
  },
  '& .MuiInputLabel-shrink': {
    //need to stay in px
    transform: `translate(14px, ${theme.spacing(-9)}) scale(0.75)`,
    opacity: '100%',
    top: '0',
    span: {
      fontSize: pxToRem(15),
      color: palette.text.secondary,
    },
  },
  '& label': {
    textTransform: 'capitalize',
  },
  '& .MuiInputBase-root.Mui-error, .MuiInputBase-root.Mui-error.Mui-focused fieldset': {
    borderColor: `${palette.red['400']} !important`,
    '&:hover': {
      fieldset: {
        borderColor: palette.red['400'],
      },
    },
  },
  '& .Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: palette.red['400'],
    '&:hover': {
      fieldset: {
        borderColor: palette.red['400'],
      },
    },
  },
}));

export const DateRangePickerMui: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  allowClearing = false,
  size = 'small',
  numOfCalendars = 1,
  onError,
  disableFuture = false,
  onClose,
}) => {
  const { t } = useTranslation();
  return (
    <BoxStyled data-testid="dateRangePickerContainer">
      <DateRangePicker
        value={value}
        onChange={onChange}
        minDate={minDate ?? undefined}
        maxDate={maxDate ?? undefined}
        calendars={numOfCalendars}
        disableFuture={disableFuture}
        slotProps={{
          // @ts-expect-error -- data-testid works but MUI's types don't allow it
          popper: () => ({
            'data-testid': 'calendar-tooltip',
          }),
          // @ts-expect-error -- data-testid works but MUI's types don't allow it
          desktopPaper: () => ({
            'data-testid': 'calendar',
          }),
          // @ts-expect-error -- data-testid works but MUI's types don't allow it
          calendarHeader: () => ({
            'data-testid': 'calendar header',
          }),
          // @ts-expect-error -- data-testid works but MUI's types don't allow it
          day: () => ({
            'data-testid': 'calendar day',
          }),
          textField: ({ position }) => ({
            label:
              position === 'start' ? (
                <Typography variant="text1" color="text.primary" data-testid="fromLabel">
                  {t('common.from')}
                </Typography>
              ) : (
                <Typography variant="text1" color="text.primary" data-testid="toLabel">
                  {t('common.to')}
                </Typography>
              ),
            'data-testid': position === 'start' ? 'fromContainer' : 'toContainer',
            className: size,
            InputProps: {
              'data-testid': position === 'start' ? 'fromInput' : 'toInput',
              name: position === 'start' ? 'from' : 'to',
              endAdornment: (
                <Box className="iconBox">
                  <DateRangeIcon
                    data-testid={position === 'start' ? 'fromIcon' : 'toIcon'}
                    className="calendarIcon"
                    width={pxToRem(22)}
                    height={pxToRem(22)}
                    sx={{ color: ({ palette }) => palette.text.secondary }}
                  />
                </Box>
              ),
            },
          }),
        }}
        onError={onError}
        onClose={onClose}
      />
      {allowClearing && value.length === 2 && (value[0] || value[1]) && (
        <ClearIcon
          data-testid="clearIcon"
          onClick={() => onChange([null, null])}
          className="clearIcon"
          sx={{ color: ({ palette }) => palette.text.secondary }}
        />
      )}
    </BoxStyled>
  );
};
